<script setup>
/**
 * "HAND CHATS TO" (spec §1 P-1.3): who gets a chat the assistant passes on.
 *
 * Three choices. "Whoever is online" and "A team" still need one PERSON, because A2's handover
 * falls back to her when nobody online is picked (a team assignment alone alerts nobody). So
 * the person box is always there, labelled "Who" for "A person" and "If nobody is online, give
 * it to" for the other two.
 *
 * Only a COMPLETE setting is emitted: a team without its team, or any choice without a person,
 * is not a value the bar can save, so the page's saved state stays as it was until it is.
 *
 * The people come from Chatwoot, read by the BFF as the owner (`GET …/handover/people`),
 * without the broadcast service user. A person who cannot see a channel the assistant answers
 * gets a warning, because Chatwoot will not show them that channel's chats.
 *
 * Task review item 11: the labels every handover adds beside `needs-a-person`, picked from the
 * account's own labels (`labelOptions`, Chatwoot's store, read by the page). They ride the same
 * value, so they are saved with the choice above.
 */
import { computed, ref, watch } from 'vue';

import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import WasloSelect from './WasloSelect.vue';
import HandoverLabels from './HandoverLabels.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { wasloApi } from '../api/client.js';
import { noteLangOf } from '../helpers/hand-to.js';

const props = defineProps({
  /** `{ to, personId, teamId, noteLang, labels }` or null (nobody chosen). */
  modelValue: { type: Object, default: null },
  accountId: { type: Number, default: null },
  /** The account's own label titles, for "Add labels". */
  labelOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const { t, locale } = useWasloI18n();

const people = useWasloApi(
  signal => wasloApi.getHandoverPeople(props.accountId, signal),
  { isEmpty: () => false }
);

const draft = ref({ to: null, personId: null, teamId: null, labels: [] });
watch(
  () => props.modelValue,
  value => {
    draft.value = {
      to: value?.to ?? null,
      personId: value?.personId ?? null,
      teamId: value?.teamId ?? null,
      labels: Array.isArray(value?.labels) ? [...value.labels] : [],
    };
  },
  { immediate: true }
);

const CHOICES = ['anyone', 'person', 'team'];
const choices = computed(() =>
  CHOICES.map(id => ({
    id,
    label: t(`WASLO.PERSONALITY.HAND_TO.${id.toUpperCase()}`),
    description: t(`WASLO.PERSONALITY.HAND_TO.${id.toUpperCase()}_BODY`),
  }))
);

const agents = computed(() => people.data.value?.agents ?? []);
const teams = computed(() => people.data.value?.teams ?? []);
const personOptions = computed(() =>
  agents.value.map(agent => ({ value: String(agent.id), label: agent.name }))
);
const teamOptions = computed(() =>
  teams.value.map(team => ({ value: String(team.id), label: team.name }))
);

const complete = next =>
  next.to !== null &&
  next.personId !== null &&
  (next.to !== 'team' || next.teamId !== null);

const set = patch => {
  const next = { ...draft.value, ...patch };
  draft.value = next;
  if (!complete(next)) return;
  emit('update:modelValue', {
    to: next.to,
    personId: next.personId,
    teamId: next.to === 'team' ? next.teamId : null,
    noteLang: noteLangOf(locale.value),
    labels: [...next.labels],
  });
};

const select = id => {
  if (CHOICES.includes(id)) set({ to: id });
};
const selectPerson = value => set({ personId: Number(value) || null });
const selectTeam = value => set({ teamId: Number(value) || null });
const selectLabels = labels => set({ labels });

const personLabel = computed(() =>
  draft.value.to === 'person'
    ? t('WASLO.PERSONALITY.HAND_TO.PERSON_LABEL')
    : t('WASLO.PERSONALITY.HAND_TO.FALLBACK_LABEL')
);

/** The chosen person misses a channel the assistant answers: say which. */
const notMember = computed(() => {
  const chosen = agents.value.find(agent => agent.id === draft.value.personId);
  if (!chosen || chosen.missingInboxes.length === 0) return '';
  const channels = chosen.missingInboxes
    .map(inbox => inbox.name)
    .filter(Boolean)
    .join(t('WASLO.COMMON.LIST_SEPARATOR'));
  return t('WASLO.PERSONALITY.HAND_TO.NOT_MEMBER', {
    name: chosen.name,
    channels,
  });
});
</script>

<template>
  <div class="flex flex-col gap-6" data-waslo-hand-to>
    <SettingsHeader
      :heading="t('WASLO.PERSONALITY.HAND_TO.HEADING')"
      :description="t('WASLO.PERSONALITY.HAND_TO.DESCRIPTION')"
    />
    <p
      v-if="modelValue === null"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-hand-to-unset
    >
      {{ t('WASLO.PERSONALITY.HAND_TO.UNSET') }}
    </p>
    <div class="flex flex-col gap-3">
      <RadioCard
        v-for="choice in choices"
        :id="choice.id"
        :key="choice.id"
        :label="choice.label"
        :description="choice.description"
        :is-active="draft.to === choice.id"
        :data-waslo-hand-to-option="choice.id"
        @select="select"
      />
    </div>
    <p
      v-if="people.error.value"
      class="mb-0 text-sm text-n-ruby-11"
      data-waslo-hand-to-failed
    >
      {{ t('WASLO.PERSONALITY.HAND_TO.PEOPLE_FAILED') }}
    </p>
    <template v-else-if="draft.to !== null">
      <WasloSelect
        v-if="draft.to === 'team'"
        :model-value="draft.teamId === null ? '' : String(draft.teamId)"
        :label="t('WASLO.PERSONALITY.HAND_TO.TEAM_LABEL')"
        :placeholder="t('WASLO.PERSONALITY.HAND_TO.CHOOSE_TEAM')"
        :options="teamOptions"
        data-waslo-hand-to-team
        @update:model-value="selectTeam"
      />
      <WasloSelect
        :model-value="draft.personId === null ? '' : String(draft.personId)"
        :label="personLabel"
        :placeholder="t('WASLO.PERSONALITY.HAND_TO.CHOOSE')"
        :options="personOptions"
        data-waslo-hand-to-person
        @update:model-value="selectPerson"
      />
      <p
        v-if="notMember"
        class="mb-0 text-sm text-n-amber-11"
        dir="auto"
        data-waslo-hand-to-not-member
      >
        {{ notMember }}
      </p>
      <HandoverLabels
        :model-value="draft.labels"
        :options="labelOptions"
        @update:model-value="selectLabels"
      />
    </template>
  </div>
</template>
